<template>
  <v-dialog
    :value="value"
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
  >
    <v-card tile>
      <v-toolbar dark>
        <v-toolbar-title> {{ $t("views.documentation.analyze_ai.title") }} </v-toolbar-title>
        <v-spacer></v-spacer>

        <v-btn
          icon
          @click="onClose"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="mt-4">
        <v-row>
          <v-col cols="8">
            <v-select
              v-model="selectedAlgo"
              :items="itemsIA"
              :label="$t('views.documentation.analyze_ai.algorithm')"
              outlined
              dense
            ></v-select>
          </v-col>
          <v-col
            cols="4"
            class="text-right"
          >
            <v-btn
              v-if="canStartAnalyze"
              color="primary"
              @click="analyzeImage"
            >
              {{ $t("views.documentation.analyze_ai.start") }}
            </v-btn>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6">
            <v-slider
              v-model="precisionAlgo"
              max="100"
              min="0"
              label="Precision"
              thumb-label="always"
              default="50"
              persistent-hint
              outlined
              dense
            ></v-slider>
          </v-col>
          <v-col
            cols="6"
            v-if="hasResult"
            class="text-center"
          >
            <span
              v-if="selectedAlgo != 'Crack'"
              class="primary--text font-weight-bold"
              style="font-size: 16px"
              >{{
                $t("views.documentation.analyze_ai.result", {
                  algorithm: selectedAlgo,
                  precision: precision
                })
              }}</span
            >
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6">
            <v-img
              id="source"
              class="img"
              contain
              :src="url"
            ></v-img>
            <h4 class="text-center">
              {{ $t("views.documentation.analyze_ai.original_img") }}
            </h4>
          </v-col>
          <v-col
            align-self="center"
            class="text-center"
            cols="6"
          >
            <v-progress-circular
              v-if="isCalculating"
              indeterminate
              color="primary"
            ></v-progress-circular>
            <canvas
              v-if="hasResult"
              id="canvas"
              class="img"
            ></canvas>
            <h4
              v-if="hasResult"
              class="text-center"
            >
              {{ $t("views.documentation.analyze_ai.mask_img") }}
            </h4>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions style="display: flex; justify-content: flex-end">
        <v-btn
          v-if="hasResult"
          color="primary"
          @click="saveMaskImage"
        >
          {{ $t("views.documentation.analyze_ai.save_img_mask") }}
        </v-btn>
        <v-btn
          v-if="hasResult"
          color="primary"
          @click="createNC"
        >
          {{
            $t("actions.create", { entity: $tc("categories.nonconformity.name", 1).toLowerCase() })
          }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import DocumentationService from "@/services/DocumentationService"
import { downloadRaw, getFilename } from "@/utils/download"

export default {
  name: "DialogIA",
  props: {
    item: {
      type: Object
    },
    value: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    canStartAnalyze() {
      return !!this.selectedAlgo
    },
    hasResult() {
      return this.precision !== null
    },
    url() {
      return this.item.file_url
    },
    projectId() {
      return this.$store.state.app.project.id
    }
  },
  data() {
    return {
      // itemsIA: ["Crack", "Rust", "Welding",],
      itemsIA: ["Crack detection", "Rust"],
      isCalculating: false,
      options: {
        cssMaxWidth: 800,
        cssMaxHeight: 800
      },
      precision: null,
      precisionAlgo: 50,
      selectedAlgo: null
    }
  },
  methods: {
    analyzeImage() {
      this.isCalculating = true
      this.precision = null
      let datas = {
        algorithm: this.selectedAlgo,
        parameters: { url_file: this.url, precision: this.precisionAlgo }
      }
      DocumentationService.analyzeImage(this, this.projectId, datas)
        .then(response => {
          if (
            ["Crack detection"].includes(this.selectedAlgo) ||
            ["Rust"].includes(this.selectedAlgo)
          ) {
            // Créer un nouvel élément image

            const blob = new Blob([response], {
              type: "image/png"
            })

            var img = new Image()
            img.onload = function () {
              // Sélectionner l'élément canvas
              var canvas = document.getElementById("canvas")
              canvas.width = img.width
              canvas.height = img.height
              // Obtenir le contexte de dessin 2D du canvas
              var ctx = canvas.getContext("2d")
              // Dessiner l'image sur le canvas
              ctx.drawImage(img, 0, 0)
            }

            // Créer un objet URL à partir du blob reçu
            const url = URL.createObjectURL(blob)
            img.src = url
            this.isCalculating = false
            this.precision = this.precisionAlgo
          } else {
            // Build/show image with mask
            let mask = response["data"]["mask"]
            let size = response["data"]["size"]
            if (["Rust", "Welding"].includes(this.selectedAlgo)) {
              this.buildMaskRust(mask, size)
              this.precision = Number.parseFloat(response["data"]["precision"]).toFixed(2)
            } else {
              this.buildMaskCrack(mask, size)
              this.precision = 0
            }
          }
        })
        .catch(err => {
          if (err.response && err.response.status === 403) {
            this.$store.commit("app/message", {
              msg: this.$t("messages.error.permission_denied"),
              color: "error"
            })
          }
        })
    },
    getOriginalImage(size) {
      let [height, width] = size
      const originalImage = new Image(width, height)
      originalImage.crossOrigin = "anonymous"
      originalImage.src = this.url
      return originalImage
    },

    async buildMaskCrack(mask, size) {
      let [height, width] = size
      const originalImage = this.getOriginalImage(size)

      // On Image load event render in canvas
      originalImage.onload = function () {
        // Get canvas where to display image
        const canvas = document.getElementById("canvas")
        canvas.width = width
        canvas.height = height

        // Get rendering context
        const ctx = canvas.getContext("2d")
        ctx.globalAlpha = 0.6

        const maskDataArray = new Uint8ClampedArray(4 * width * height)
        for (let i = 0; i < mask.length; i++) {
          maskDataArray[4 * i] = 0
          maskDataArray[4 * i + 1] = mask[i] * 255
          maskDataArray[4 * i + 2] = 0
          maskDataArray[4 * i + 3] = 255
        }

        const maskImageData = new ImageData(maskDataArray, width, height, {
          colorSpace: "srgb"
        })
        ctx.putImageData(maskImageData, 0, 0, 0, 0, width, height)

        ctx.drawImage(originalImage, 0, 0, width, height, 0, 0, width, height)
      }
      this.isCalculating = false
      return false
    },

    async buildMaskRust(mask, size) {
      let [height, width] = size

      // Pre-format mask data
      const maskDataArray = new Uint8ClampedArray(4 * width * height)
      for (let i = 0; i < mask.length; i++) {
        maskDataArray[4 * i] = 0
        maskDataArray[4 * i + 1] = mask[i]
        maskDataArray[4 * i + 2] = 0
        maskDataArray[4 * i + 3] = 255
      }

      // Create image data from mask data
      const maskImageData = new ImageData(maskDataArray, width, height, { colorSpace: "srgb" })

      // Create original image from file url
      const originalImage = this.getOriginalImage(size)

      // On Image load event render in canvas
      originalImage.onload = function () {
        // Get canvas where to display image
        const canvas = document.getElementById("canvas")
        canvas.width = width
        canvas.height = height

        // Get rendering context
        const ctx = canvas.getContext("2d")
        ctx.globalAlpha = 0.6
        ctx.putImageData(maskImageData, 0, 0, 0, 0, width, height)
        ctx.drawImage(originalImage, 0, 0, width, height, 0, 0, width, height)
      }

      this.isCalculating = false
    },
    clear() {
      this.isCalculating = false
      this.precision = null
      this.precisionAlgo = 50
      this.selectedAlgo = null

      let element = document.getElementById("canvas")
      if (element) {
        let context = element.getContext("2d")
        context.clearRect(0, 0, element.width, element.height)
      }
    },
    createNC() {
      this.$router.push({ path: "/project/non-conformities/editor" })
    },
    saveMaskImage() {
      const canvas = document.getElementById("canvas")
      const data = canvas.toDataURL("image/png")
      downloadRaw(data, getFilename("png"))
    },
    onClose() {
      this.clear()
      this.$emit("close", false)
    }
  }
}
</script>

<style lang="scss" scoped>
.img {
  max-height: 50vh;
  max-width: 100%;
}
</style>
